import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { IMAGES, PRINCIPAL } from "@/data";
import { Reveal, SectionHead } from "@/components/Reveal";

const CHAPTERS = [
  {
    num: "01",
    title: "Hello from Coranderrk Street",
    text: `Glebe Park Dental has been looking after people in the city since 1989. ${PRINCIPAL} and Dr Peter Stejskal share the rooms here — two dentists with the same idea: that a dentist should treat you like a grown-up who can handle the full picture, then give you time to decide.`,
  },
  {
    num: "02",
    title: "Straight talk, no upselling",
    text: "Before anything happens, you'll get a clear rundown: what we found, what actually needs doing, what can wait, and what it'll cost. If there's a cheaper option that works just as well, we'll say so. You decide — we just make sure you've got the full picture.",
  },
  {
    num: "03",
    title: "The thing patients mention most",
    text: "That it doesn't feel like a dentist's. Appointments run to time, nobody's rushed, and the view over Glebe Park doesn't hurt either. It's an easy one to fit around work — plenty of people duck over from the office in a lunch break.",
  },
];

const Manifesto = () => (
  <section id="about" data-testid="manifesto-section" className="bg-cream py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
      <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <SectionHead
            dark
            overline="Hello from Coranderrk Street"
            title={
              <>
                The city practice Canberra has{" "}
                <span className="italic text-fern">trusted since 1989.</span>
              </>
            }
          />
          <Reveal delay={0.2} className="relative mt-12">
            <div className="absolute -left-3 -top-3 h-full w-full rounded-2xl border border-leaf/40" />
            <img
              src={IMAGES.interior}
              alt="Treatment room at Glebe Park Dental, Coranderrk Street"
              className="relative aspect-[4/3] w-full rounded-2xl object-cover object-right shadow-2xl"
            />
            <div className="absolute -bottom-6 -right-3 rounded-xl bg-forest px-6 py-4 shadow-xl sm:-right-6">
              <p className="font-serif text-3xl text-cream">1989</p>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-mist">
                Looking after Canberra since
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7 lg:pt-16">
          <Reveal>
            <Link to="/about" data-testid="about-meet-team-link" className="group mb-6 inline-flex items-center gap-2 font-sans text-sm font-medium text-ink transition-colors hover:text-leafdeep lg:px-6">
              Meet Dr Gupta and Dr Stejskal <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={0.12 * i}>
              <div className="group flex gap-8 border-t border-ink/10 py-10 transition-colors duration-500 first:border-t-0 hover:bg-sage/60 lg:px-6">
                <span className="font-serif text-5xl leading-none text-stone/50 transition-colors duration-500 group-hover:text-leaf sm:text-6xl">
                  {c.num}
                </span>
                <div>
                  <h3 className="font-serif text-xl leading-snug text-ink sm:text-2xl">{c.title}</h3>
                  <p className="mt-4 max-w-lg font-sans text-sm leading-relaxed text-fern sm:text-base">
                    {c.text}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default Manifesto;
